'use client';

import { isEmpty } from 'lodash';
import keyBy from 'lodash/keyBy';
import mapValues from 'lodash/mapValues';
import pickBy from 'lodash/pickBy';
import range from 'lodash/range';
import { FC, useCallback, useMemo } from 'react';
import { CutSetup, ElasticCourseData, ElasticMatchplayMatchesMatchesInner, ElasticTournament, TournamentEventTypeEnum, TournamentRequestFourpeopleTypesEnum } from '@spikemark/rest-api';
import { ClientTime } from '@spikemark/ui/components/client-time';
import { MascotWithName } from '@spikemark/ui/components/mascot-with-name';
import { PositionDeltaDisplay } from '@spikemark/ui/components/position-delta-display';
import { RoundSelector } from '@spikemark/ui/components/round-selector';
import { ScoreToPar } from '@spikemark/ui/components/score-to-par';
import { CourseLabelDisplay } from '@spikemark/ui/components/table/leaderboard/columns/shared';
import { Table } from '@spikemark/ui/components/table/table';
import { TableColumn } from '@spikemark/ui/components/table/types';
import { LeaderboardDisplayMode } from '@spikemark/ui/types/leaderboard';
import { cn } from '@spikemark/ui/utils/cn';
import { strokesToDisplayValue } from '@spikemark/ui/utils/format';
import { isSubstitute } from '@spikemark/ui/utils/player';
import { getLeaderboardRankingDisplay } from '@spikemark/ui/utils/scoring';
import { getRoundStatus } from '@spikemark/ui/utils/tournament';
import { createScorecardRowColumns } from './scorecard-columns';
import { ScorecardCourseLegend, ScorecardCourseLegendProvider, useScorecardCourseLegendContext } from './scorecard-course-legend';
import { useScorecardDialogContext } from './scorecard-dialog-provider';
import { ScorecardRow } from './types';
export type HoleScorecardProps = {
  tournament: ElasticTournament;
  roundId: string;
  numRounds: number;
  scoresCount?: number;
  courseInfoMap: Record<string, ElasticCourseData>;
  mode: LeaderboardDisplayMode;
  eventType: TournamentEventTypeEnum;
  matchplayFormat?: TournamentRequestFourpeopleTypesEnum;
  isAdmin?: boolean;
  onRoundIdChange?: (roundId: string) => void;
  suppressRank?: boolean;
  data: ScorecardRow[];
  match?: ElasticMatchplayMatchesMatchesInner;
  cutSetup?: CutSetup;
};
const HoleScorecardBody: FC<HoleScorecardProps> = ({
  tournament,
  data,
  scoresCount,
  roundId,
  numRounds,
  courseInfoMap: _courseInfoMap,
  mode,
  eventType: format,
  matchplayFormat,
  isAdmin,
  suppressRank,
  match,
  cutSetup,
  onRoundIdChange
}) => {
  const {
    setSelectedCourseLabel
  } = useScorecardCourseLegendContext();
  const {
    showPlayerScorecard
  } = useScorecardDialogContext();
  const roundIdAsNumber = parseInt(roundId, 10);
  const courseInfoMap = useMemo(() => {
    const playersByCourseLabel = keyBy(data, x => `${roundId}-${x.courseLabels?.[roundIdAsNumber - 1] ?? 'A'}`);
    return pickBy(_courseInfoMap, (value, key) => !!playersByCourseLabel[key]);
  }, [_courseInfoMap, data]);
  const roundIsMultiCourse = Object.keys(courseInfoMap ?? {}).filter(x => x.startsWith(String(roundId))).length > 1;
  const holes = data?.[0]?.holes ?? range(1, 19);
  const rowCount = data.length;
  const primaryCourse = Object.values(courseInfoMap)[0];
  const courseLegend = roundIsMultiCourse ? <ScorecardCourseLegend childrenByCourseLabel={mapValues(courseInfoMap, x => <div className="w-full flex items-center">
          <div className="flex-1 mr-4 text-left line-clamp-1">{x.courseName}</div>
          <CourseLabelDisplay courseInfoMap={courseInfoMap} courseLabels={[x.courseLabel ?? 'A']} roundId={roundIdAsNumber} suppressTooltip />
        </div>)} /> : <div />;
  const handleRoundIdChange = useCallback((roundId: number) => {
    onRoundIdChange?.(String(roundId));
  }, [onRoundIdChange]);
  const columns = useMemo(() => {
    const rankings = data?.map(({
      currentRank
    }) => currentRank) ?? [];
    const columns: TableColumn<ScorecardRow>[] = [{
      key: 'currentRank',
      cellClassName: 'numeric min-w-10 lg:min-w-12',
      sticky: true,
      width: '1%',
      headerCellClassName: '!text-left px-4',
      header: typeof onRoundIdChange === 'function' ? <RoundSelector rounds={Array.apply(null, Array(numRounds || 1)).map((_, i) => i + 1).filter(roundId => getRoundStatus(tournament, roundId) === 'ACTIVE')} currentRoundId={roundIdAsNumber} onSelect={handleRoundIdChange} /> : null,
      subHeader: courseLegend,
      headerColSpan: 3,
      subHeaderColSpan: 3,
      hidden: suppressRank,
      cellRenderer: ({
        rowData
      }) => {
        if (!cutSetup || isEmpty(cutSetup.teams) && isEmpty(cutSetup.players)) {
          return getLeaderboardRankingDisplay(rankings, rowData.currentRank);
        }
        const isTeamCut = !cutSetup?.teams?.includes(rowData.schoolId);
        const isPlayerAdvanced = rowData.playerId && cutSetup?.players?.includes(rowData.playerId);
        if (isTeamCut && !isPlayerAdvanced) {
          return 'CUT';
        }
        return getLeaderboardRankingDisplay(rankings, rowData.currentRank);
      }
    }, {
      key: 'rankDelta',
      cellRenderer: ({
        rowData
      }) => rowData.type === 'MATCHPLAY_DELTA' ? null : <PositionDeltaDisplay change={rowData.lastRank === 0 ? 0 : rowData.rankDelta || 0} />,
      cellClassName: 'numeric',
      sticky: true,
      width: '1%',
      hidden: suppressRank
    }, {
      width: '22%',
      cellClassName: 'pr-4 min-w-[140px] relative',
      sticky: true,
      cellStyle: ({
        rowData
      }) => {
        const playerIsWinning = match?.players.find(x => x.playerId === rowData.playerId)?.playerResult === 'Win';
        return {
          borderColor: rowData.schoolColor ?? 'transparent',
          borderLeftWidth: format === 'Stroke Play' ? undefined : 4,
          backgroundColor: playerIsWinning ? rowData.schoolColor : 'bg-white'
        };
      },
      cellRenderer: ({
        rowData,
        rowIndex
      }) => {
        if (rowData.type === 'MATCHPLAY_DELTA') {
          return '';
        }
        if (rowData.playerId) {
          const currentRound = rowData.rounds?.find(x => x.roundId === roundIdAsNumber);
          const playerIsWinning = match?.players.find(x => x.playerId === rowData.playerId)?.playerResult === 'Win';
          return <div className={cn('flex items-center', {
            dark: playerIsWinning
          })}>
                <MascotWithName containerClassName="flex-1 leading-tight" variant="small" alt={rowData.playerName ?? ''} path={rowData.userPicture} displayMode="player">
                  <span className="dark:text-white font-bold">
                    {rowData.playerName}
                    {rowData.teamLabel === 'IND' ? ' (IND)' : ''}
                    {isSubstitute(rowData, roundIdAsNumber) ? ' (SUB)' : ''}
                  </span>
                </MascotWithName>
                {currentRound?.thru === '0' && currentRound.teeTime ? <div className="text-xs text-gray-500 dark:text-gray-100 ml-2">
                    <ClientTime time={currentRound.teeTime} />
                  </div> : null}
                {roundIsMultiCourse && <CourseLabelDisplay courseInfoMap={courseInfoMap} courseLabels={rowData.courseLabels} roundId={roundIdAsNumber} />}
                {playerIsWinning && <div className="h-full w-4 absolute right-0">
                    <svg className="h-full w-full" viewBox={matchplayFormat === 'SINGLES' ? '0 0 10 10' : [0, 3].includes(rowIndex) ? '0 0 10 5' : '0 5 10 5'} preserveAspectRatio="none">
                      <path className="fill-white" d="M0,0 L10,0 L10,5 L10,10 L0,10 L10,5Z" />
                    </svg>
                    {rowIndex === 0 ? <div className="h-[1px] bg-gray-300 absolute top-0 left-0 right-0 z-1" /> : null}
                  </div>}
              </div>;
        }
        return <MascotWithName variant="small" alt={rowData.schoolName} path={rowData.schoolLogo}>
              {rowData.schoolName}
            </MascotWithName>;
      },
      header: <span className="font-normal">Holes</span>,
      subHeader: suppressRank ? courseLegend : undefined
    }, {
      header: 'Total',
      key: 'totalScore',
      cellClassName: 'numeric border-l border-r px-1 lg:px-2 xl:px-3',
      cellRenderer: ({
        rowData
      }) => isSubstitute(rowData, roundIdAsNumber) ? 'SUB' : mode === 'score' ? <ScoreToPar score={rowData.totalScore} square /> : strokesToDisplayValue(rowData.totalStrokes),
      width: 55,
      sortable: true,
      hidden: suppressRank
    }, ...createScorecardRowColumns({
      mode: 'stroke',
      roundId,
      courseInfoMap,
      scoresCount,
      format,
      holes,
      rowCount,
      matchplayFormat,
      match
    })];
    return columns;
  }, [data, onRoundIdChange, numRounds, roundIdAsNumber, handleRoundIdChange, courseLegend, suppressRank, roundId, courseInfoMap, scoresCount, format, holes, rowCount, matchplayFormat, match, tournament, cutSetup, roundIsMultiCourse, mode]);
  return <div data-sentry-component="HoleScorecardBody" data-sentry-source-file="hole-scorecard.tsx">
      {!roundIsMultiCourse && primaryCourse ? <div className="px-1.5 lg:px-3.5 py-2 bg-gray-50 border-t border-gray-300 text-xs lg:text-sm">
          {primaryCourse.courseName}
        </div> : null}
      <Table<ScorecardRow> columns={columns} data={data} compact className="border-t border-gray-300 w-full" headerRowClassName="bg-gray-50 pt-2" rowClassName={({
      rowIndex,
      rowData
    }) => cn('bg-white', {
      'border-t border-gray-200': typeof scoresCount === 'number' && rowIndex === scoresCount,
      'hover:bg-gray-100 transition-colors duration-200 cursor-pointer': format !== 'Match Play' && rowData.type !== 'MATCHPLAY_DELTA',
      'text-gray-400': isSubstitute(rowData, roundIdAsNumber)
    })} headerCellClassName="lg:first:pl-3 pt-1 pb-0 header-cell" cellClassName="lg:first:pl-3" expandedRowClassName="bg-gray-50" subHeaderRowClassName="bg-gray-50 shadow-keyline-b" subHeaderCellClassName="lg:first:pl-3 py-0" stickyLeftClassName="left-[-2px]" allowHorizontalScroll rowKey={({
      rowData
    }) => rowData.playerId ?? rowData.schoolId} onRowMouseEnter={roundIsMultiCourse ? ({
      rowData
    }) => {
      if (rowData.playerId) {
        setSelectedCourseLabel(rowData.courseLabels?.[0]);
      }
    } : undefined} onRowMouseLeave={roundIsMultiCourse ? ({
      rowData
    }) => {
      if (rowData.playerId) {
        setSelectedCourseLabel(undefined);
      }
    } : undefined} onRowClick={({
      rowData
    }) => {
      if (format !== 'Match Play' && rowData.type !== 'MATCHPLAY_DELTA') {
        showPlayerScorecard(rowData.playerId!);
      }
    }} data-sentry-element="Table" data-sentry-source-file="hole-scorecard.tsx" />
    </div>;
};
export const HoleScorecard: FC<HoleScorecardProps> = props => <ScorecardCourseLegendProvider roundId={props.roundId} data-sentry-element="ScorecardCourseLegendProvider" data-sentry-component="HoleScorecard" data-sentry-source-file="hole-scorecard.tsx">
    <HoleScorecardBody {...props} data-sentry-element="HoleScorecardBody" data-sentry-source-file="hole-scorecard.tsx" />
  </ScorecardCourseLegendProvider>;